import { Divider, Tag } from "antd";
import dayjs from "dayjs";
import {
  Activity,
  Calendar,
  Clock,
  FileText,
  Globe,
  GraduationCap,
  Hash,
  Heart,
  KeyRound,
  Mail,
  MapPin,
  Monitor,
  ScanFace,
  Shield,
  Smartphone,
  User,
  UserCircle,
} from "lucide-react";

import type { AccountDetailVO } from "@/api/accounts/type";
import { EmptyBlock, SectionTitle } from "@/components/DetailBlocks";
import { getTerminalLabel } from "@/config/system";
import { useDict } from "@/hooks/useDict";
import { ENABLE_STATUS_CONFIG } from "@/types";
import { emptyPlaceholder } from "@/utils";

import { DetailInfoRow } from "./detailParts";

/** 基本信息区块 */
const BasicInfoSection = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => {
  const st = accountDetail && ENABLE_STATUS_CONFIG[accountDetail.status];
  return (
    <div>
      <SectionTitle icon={UserCircle} title="基本信息" />
      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
        <DetailInfoRow icon={Hash} label="账号ID" value={accountDetail?.userCode} mono />
        <DetailInfoRow icon={User} label="姓名" value={emptyPlaceholder(accountDetail?.name)} />
        <DetailInfoRow icon={Smartphone} label="手机号" value={accountDetail?.phone} />
        <DetailInfoRow
          icon={Mail}
          label="邮箱"
          value={emptyPlaceholder(accountDetail?.profile?.email)}
        />
        <div className="flex items-center gap-1.5 text-sm">
          <Activity className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-500 w-24 shrink-0">账号状态</span>
          {st && (
            <Tag bordered color={st.color}>
              {st.label}
            </Tag>
          )}
        </div>
        <DetailInfoRow
          icon={KeyRound}
          label="密码设置"
          value={accountDetail?.passwordSet ? "已设置" : "未设置"}
        />
        <DetailInfoRow
          icon={Globe}
          label="创建来源"
          value={emptyPlaceholder(
            getTerminalLabel(accountDetail?.createSource?.toUpperCase()) ||
              accountDetail?.createSource,
          )}
        />
        <DetailInfoRow
          icon={Shield}
          label="默认身份"
          value={emptyPlaceholder(accountDetail?.defaultRole)}
        />
        <DetailInfoRow
          icon={Clock}
          label="创建时间"
          value={
            accountDetail?.createAt
              ? dayjs(accountDetail.createAt).format("YYYY-MM-DD HH:mm:ss")
              : "-"
          }
        />
        <DetailInfoRow
          icon={Clock}
          label="最后登录"
          value={
            accountDetail?.lastLoginAt
              ? dayjs(accountDetail.lastLoginAt).format("YYYY-MM-DD HH:mm:ss")
              : "-"
          }
        />
        <DetailInfoRow
          icon={Monitor}
          label="登录终端"
          value={getTerminalLabel(accountDetail?.lastLoginSystemPoint?.toUpperCase()) || "-"}
        />
      </div>
    </div>
  );
};

/** 实名认证区块 */
const RealNameSection = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => {
  const np = accountDetail?.realName;
  const { getDictLabel } = useDict();
  return (
    <div>
      <SectionTitle icon={ScanFace} title="实名认证" />
      {!np ? (
        <EmptyBlock text="未完成实名认证" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            <DetailInfoRow icon={User} label="真实姓名" value={np.realNameMask} />
            <DetailInfoRow
              icon={FileText}
              label="证件类型"
              value={getDictLabel("userIdType", np.idType)}
            />
            <DetailInfoRow icon={FileText} label="证件号码" value={np.idNumberMask} mono />
            <DetailInfoRow
              icon={Shield}
              label="认证状态"
              value={emptyPlaceholder(getDictLabel("userRealNameStatus", np.verifyStatus))}
            />
            <DetailInfoRow
              icon={Clock}
              label="认证时间"
              value={np.verifiedAt ? dayjs(np.verifiedAt).format("YYYY-MM-DD HH:mm:ss") : undefined}
            />
            <DetailInfoRow
              icon={Globe}
              label="认证渠道"
              value={emptyPlaceholder(getDictLabel("userRealNameProvider", np.verifyProvider))}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/** 个人资料区块 */
export const ProfileSection = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => {
  const { getDictLabel } = useDict();
  const p = accountDetail?.profile;
  return (
    <div>
      <SectionTitle icon={FileText} title="个人资料" />
      {!p ? (
        <EmptyBlock text="个人资料未完善" />
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
          <DetailInfoRow icon={UserCircle} label="昵称" value={emptyPlaceholder(p.nickname)} />
          <DetailInfoRow
            icon={User}
            label="性别"
            value={emptyPlaceholder(getDictLabel("gender", p.gender))}
          />
          <DetailInfoRow icon={Calendar} label="生日" value={emptyPlaceholder(p.birthday)} />
          <DetailInfoRow
            icon={GraduationCap}
            label="学历"
            value={emptyPlaceholder(getDictLabel("education", p.education))}
          />
          <DetailInfoRow
            icon={Heart}
            label="婚姻状况"
            value={emptyPlaceholder(getDictLabel("marital", p.maritalStatus))}
          />
          <DetailInfoRow
            icon={Smartphone}
            label="联系手机"
            value={emptyPlaceholder(accountDetail.phone)}
          />
          <DetailInfoRow icon={Mail} label="联系邮箱" value={emptyPlaceholder(p.email)} />
          <DetailInfoRow icon={MapPin} label="通信地址" value={emptyPlaceholder(p.address)} full />
        </div>
      )}
    </div>
  );
};

/** Tab：账号信息（基本 / 实名 / 个人资料） */
export const BasicInfoTab = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => (
  <div className="space-y-5">
    <BasicInfoSection accountDetail={accountDetail} />
    <Divider className="m-0" />
    <RealNameSection accountDetail={accountDetail} />
    <Divider className="m-0" />
    <ProfileSection accountDetail={accountDetail} />
  </div>
);
